import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { Client } from '../common/types';

interface DeleteClientModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  client: Client | null;
  handleDeleteClient: () => void;
}

export default function DeleteClientModal({
  isOpen,
  setIsOpen,
  client,
  handleDeleteClient
}: DeleteClientModalProps) {
  const expectedDeletionString = `DELETE ${client?.name}`.toUpperCase();
  const [deletionString, setDeletionString] = useState('');

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
        {/* Grayed-out background */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        {/* Core Modal */}
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                id="add-client-modal"
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all my-8 w-full max-w-md sm:p-12"
              >
                <div>
                  <div className="text-center">
                    {/* Modal title */}
                    <Dialog.Title as="h3" className="text-xl font-bold leading-6 text-gray-900">
                      Delete Client
                    </Dialog.Title>

                    {/* Modal body */}
                    <div className="mt-8 text-left sm:w-full mt-2 text-gray-700">
                      <p>
                        Are you sure you want to delete <b>{client?.name}</b>? All of the client's
                        meeting notes will also be deleted. <b>This action cannot be undone.</b>
                      </p>

                      <p className="mt-4">
                        {' '}
                        Please type "<i>{expectedDeletionString}</i>" below to confirm.
                      </p>
                      <input
                        onChange={(e) => setDeletionString(e.target.value)}
                        className="mt-4 block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />

                      <div>
                        <button
                          disabled={!(client && expectedDeletionString === deletionString)}
                          onClick={handleDeleteClient}
                          type="submit"
                          className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 mt-12 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          Delete Client
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
